var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { concatURI, ReturnCode } from '../index';
import { config, shouldCheck, isInFrame } from '../config/constants';
import { isWeixin } from '../utils/userAgent';
import { proxyApi } from './index';
import { cwLogin, LoginCode } from './wechat';
/**
 * 权限校验的状态码
 * @export
 * @enum {number}
 * @see AccessCodeText
 */
export var AccessCode;
(function (AccessCode) {
    AccessCode["Success"] = "200";
    AccessCode["Accepted"] = "202";
    AccessCode["Found"] = "302";
    AccessCode["Recommend"] = "306";
    AccessCode["Forbidden"] = "403";
})(AccessCode || (AccessCode = {}));
/**
 * 查询微站权限配置
 * @export
 * @param {*} [uaId=config.uaId]
 * @param {*} [type=config.accessType]
 */
export function findLimit(uaId = config.uaId, type = config.accessType) {
    if (uaId) {
        return proxyApi({
            module: 'site',
            target: 'findLimit',
            data: {
                type,
                uaId,
            },
            freeLogin: 1
        });
    }
    else {
        return null;
    }
}
/**
 * 验证目标的权限
 * @export
 * @param {*} [uaId=config.uaId]
 * @param {*} [type=config.accessType]
 */
export function getAccess(uaId = config.uaId, type = config.accessType) {
    if (uaId) {
        return proxyApi({
            module: 'site',
            target: 'access',
            data: {
                type,
                uaId,
            }
        });
    }
    else {
        return { code: AccessCode.Success, redirect: '' };
    }
}
/**
 * AccessCode对应的文本描述
 */
export const AccessCodeText = {
    [AccessCode.Success]: '成功',
    [AccessCode.Accepted]: '信息审核中，请耐心等待',
    [AccessCode.Found]: '填写问卷观看后续内容',
    [AccessCode.Recommend]: '暂未关注',
    [AccessCode.Forbidden]: '您暂时没有权限访问'
};
export const access = {};
/**
 * 检测权限并返回结果
 * @export
 * @param {*} [uaId=config.uaId]
 * @param {*} [type=config.accessType]
 * @param {*} [{autoReturn=false}={}] 302自动跳转带上c_default_redirect=window.location
 */
export function checkAccess(uaId = config.uaId, type = config.accessType, { autoReturn = false } = {}) {
    return __awaiter(this, void 0, void 0, function* () {
        if (uaId in access)
            return access[uaId];
        let freeLogin = false, redirect, code = AccessCode.Forbidden, text = '';
        let limit;
        let detail;
        if (shouldCheck()) {
            limit = yield findLimit(uaId, type);
            if ((limit === null || limit === void 0 ? void 0 : limit.detail) && Object.keys(limit.detail).length) {
                detail = limit === null || limit === void 0 ? void 0 : limit.detail;
            }
        }
        // 使用默认覆盖
        if (!detail) {
            detail = config.defaultAccess;
        }
        if (isWeixin()) {
            switch (detail.wechatLimit) {
                case 'userinfo':
                    window.cw('login_scope', 'snsapi_userinfo');
                    break;
                case 'base':
                    window.cw('login_scope', 'snsapi_base');
                    break;
                default:
                    freeLogin = true;
            }
        }
        else {
            switch (detail.notWechatLimit) {
                case 'userinfo':
                    window.cw('login_scope', 'snsapi_userinfo');
                    isInFrame ? window.cw('allowScanLogin', 2) : window.cw('allowScanLogin', 1);
                    break;
                case 'base':
                    window.cw('login_scope', 'snsapi_base');
                    break;
                default:
                    freeLogin = true;
            }
        }
        if (freeLogin) {
            code = AccessCode.Success;
        }
        else {
            try {
                yield cwLogin();
                /**订阅号*/
                if (shouldCheck()) {
                    const t = yield _checkAccess(uaId, type);
                    if (t.code) {
                        code = t.code;
                    }
                    if (t.redirect) {
                        redirect = concatURI(t.redirect, {
                            'c_default_redirect': autoReturn ? window.location.href : null
                        });
                    }
                    text = t.text;
                }
                else {
                    code = AccessCode.Success;
                }
            }
            catch (error) {
                if ((error === null || error === void 0 ? void 0 : error.code) === LoginCode.Subscribe) {
                    code = AccessCode.Success;
                }
            }
        }
        return (access[uaId] = {
            success: code === AccessCode.Success,
            limitType: isWeixin() ? detail.wechatLimit : detail.notWechatLimit,
            code,
            redirect,
            text: text || AccessCodeText[AccessCode.Forbidden]
        });
    });
}
/**
 * 权限校验的脚本
 * @param {string} uaId
 * @param {AccessType} [type]
 */
function _checkAccess(uaId, type) {
    return __awaiter(this, void 0, void 0, function* () {
        const accessRsp = yield getAccess(uaId, type);
        const text = accessRsp.code ? AccessCodeText[accessRsp.code] : '';
        switch (accessRsp.code) {
            case AccessCode.Accepted:
                return Object.assign(Object.assign({}, accessRsp), { text });
            case AccessCode.Success:
            case AccessCode.Found:
            case AccessCode.Recommend:
            case AccessCode.Forbidden:
        }
        return Object.assign(Object.assign({}, accessRsp), { text });
    });
}
/**
 * 在直播后台配置验证手机/邮箱才可访问，根据提交的表单信息（手机邮箱）进行校验
 * @export
 * @param {string} contact 表单提交手机号或者邮箱
 */
export function verifyContact(contact) {
    return __awaiter(this, void 0, void 0, function* () {
        if (config.uaId) {
            return proxyApi({
                module: 'site',
                target: 'verifyContact',
                data: {
                    type: 'webinar',
                    uaId: config.uaId,
                    contact
                },
                freeLogin: 1
            });
        }
        else {
            return { code: ReturnCode.Succees };
        }
    });
}
